import { getState } from '../state.js';
import { isStandalone, getStandaloneData } from '../config.js';

interface TranslationMap {
  [lang: string]: {
    [key: string]: string;
  };
}

interface TranslatableConcept {
  title: string;
  summary?: string | null;
  content?: string | null;
  translations?: { lang: string; title: string; summary?: string | null; content?: string | null }[];
}

interface TranslatableEdge {
  label: string | null;
  translations?: { lang: string; name: string }[];
}

let uiTranslations: TranslationMap | null = null;
let loadPromise: Promise<void> | null = null;

export function loadUITranslations(): Promise<void> {
  if (loadPromise) return loadPromise;
  if (isStandalone()) {
    const data = getStandaloneData() as Record<string, unknown>;
    uiTranslations = (data.uiTranslations as TranslationMap) || {};
    loadPromise = Promise.resolve();
    return loadPromise;
  }
  loadPromise = fetch('/api/translations')
    .then(r => r.json())
    .then(data => { uiTranslations = data; })
    .catch(err => {
      console.warn('Failed to load UI translations:', (err as Error).message);
      uiTranslations = {};
    });
  return loadPromise;
}

export function translate(key: string, fallback?: string): string {
  const { lang } = getState();
  if (lang && uiTranslations?.[lang]?.[key]) {
    return uiTranslations[lang][key];
  }
  return fallback !== undefined ? fallback : key;
}

export function getTranslatedFields(concept: TranslatableConcept): { title: string; summary: string | null | undefined; content: string | null | undefined } {
  const { lang } = getState();
  if (!lang || !concept.translations || concept.translations.length === 0) {
    return { title: concept.title, summary: concept.summary, content: concept.content };
  }

  const normalizedLang = lang.toLowerCase();
  const tr = concept.translations.find(row => row.lang.toLowerCase() === normalizedLang);
  if (!tr) {
    return { title: concept.title, summary: concept.summary, content: concept.content };
  }

  return {
    title: tr.title || concept.title,
    summary: tr.summary ?? concept.summary,
    content: tr.content ?? concept.content,
  };
}

export function translateTitle(concept: { slug: string; title: string }, translationsMap?: Record<string, { title: string }>): string {
  const { lang } = getState();
  if (!lang || !translationsMap) return concept.title;

  const tr = translationsMap[concept.slug];
  if (tr && tr.title) return tr.title;
  return concept.title;
}

export function getTranslatedEdgeLabel(edge: TranslatableEdge): string | null {
  const { lang } = getState();
  if (!lang || !edge.label || !edge.translations || edge.translations.length === 0) {
    return edge.label;
  }

  const normalizedLang = lang.toLowerCase();
  const tr = edge.translations.find(row => row.lang.toLowerCase() === normalizedLang);
  if (!tr || !tr.name) {
    return edge.label;
  }

  return tr.name;
}

export function pluralize(count: number, singular: string, plural: string = singular + 's'): string {
  return count === 1 ? singular : plural;
}
